/**
 * Module 10 — Settings · API service client
 *
 * One typed surface for the seven Settings submodules. Each call is routed to
 * the owning backend through the client whose proxy/auth matches that service
 * (Blueprint §5.9, 10-settings.md §"Scope/auth"):
 *
 *   - Org / Users / Departments → identity-service via `identityClient`
 *     (`/identity-api` → `/settings/*`). Auth: bearer + `x-server-key`.
 *   - AI Policy / Constitution  → harness via `apiClient`
 *     (`/context-api/api/v1/orgs/{id}/*`). Auth: bearer + `x-server-key` (+ x-user-id).
 *   - Usage & Cost / Billing    → data-service via `dataServiceClient`
 *     (`/data-api/orgs/{id}/*`). Auth: `x-server-key` (system_call).
 *
 * The org id is the tenant id from `useScopeStore().org.id`.
 */

import { apiClient } from '../client';
import { identityClient } from '../identity';
import { dataServiceClient } from '../dataservice-client';
import { getScopeHeaders } from '@/lib/stores/scope-store';
import type { Role } from './session';

const WEB_SERVER_KEY = process.env.NEXT_PUBLIC_WEB_SERVER_KEY || 'KlOtCoGnO';

/** data-service settings endpoints authorize via `system_call` (x-server-key). */
const dataAuth = { headers: { 'x-server-key': WEB_SERVER_KEY } };

/**
 * Identity responses are enveloped in `APISuccessResponse` (`{ data, message,
 * statusCode, success }`). Unwrap to the payload; tolerate a raw payload too.
 */
function idUnwrap<T>(body: any): T {
  if (body && typeof body === 'object' && 'success' in body && 'data' in body) return body.data as T;
  return body as T;
}

/** `identityClient` only sends bearer + `x-server-key`; attach the scope
 *  headers (`x-organization-id` …) so org-scoped reads resolve the tenant. */
function idConfig(extra?: Record<string, any>) {
  return { headers: getScopeHeaders(), ...extra };
}

/* ------------------------------------------------------------------ *\
   10.1 Org
\* ------------------------------------------------------------------ */

export type DeploymentMode = 'cloud' | 'on_prem' | 'air_gapped';
export type HealthStatus = 'Healthy' | 'Degraded' | 'Down';

export interface Organization {
  id: string;
  name: string;
  primaryDomain: string;
  deploymentMode: DeploymentMode;
  plan: string;
  idp?: { type: 'okta_saml' | 'oidc' | 'none'; status: 'Connected' | 'Pending' | 'Error'; protocol?: string };
  scim?: { enabled: boolean; usersSynced: number };
  environmentHealth?: { key: string; label: string; status: HealthStatus }[];
  createdAt?: string;
  updatedAt?: string;
}

export interface OrgHealth {
  orgId: string;
  components: { key: string; label: string; status: HealthStatus }[];
  checkedAt: string;
}

export interface UpdateOrgInput {
  name?: string;
  primaryDomain?: string;
  deploymentMode?: DeploymentMode;
  idp?: Organization['idp'];
  scim?: Organization['scim'];
}

/* ------------------------------------------------------------------ *\
   10.2 Users
\* ------------------------------------------------------------------ */

export type UserRole = Role;
export type UserStatus = 'Active' | 'Invited' | 'Suspended';
export type SsoStatus = 'sso' | 'password' | 'scim';

export interface SettingsUser {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  departmentId?: string;
  departmentName?: string;
  /** Project names where this user holds a project_admin role — a separate
   *  dimension from `role` above (org-level); a plain 'member' can still be
   *  project_admin of specific projects. */
  projectAdminOf?: string[];
  projectAdminIds?: string[];
  deptAdminOf?: string[];
  deptAdminIds?: string[];
  /** Plain project membership (any role) — projects this user belongs to,
   *  independent of admin status. */
  memberProjectIds?: string[];
  ssoStatus: SsoStatus;
  status: UserStatus;
  /** True when this row is a pending invitation rather than an account. A
   *  Suspended row means different things for each: an account is withheld from
   *  the list, an invitation stays listed so it can be seen and re-invited. */
  isInvite?: boolean;
  lastActive?: string;
  avatarUrl?: string;
}

export interface InviteInput {
  name?: string;
  email: string;
  // Built-in module role OR a custom org role key.
  role: UserRole | string;
  /** Required for dept_admin — the department they will administer (and whose
   *  projects they get access to). For other roles it is the home department. */
  departmentId?: string;
  /** Required for project_admin — the one project they will administer. */
  projectId?: string;
  /** dept_admin only — which of the department's projects they may administer.
   *  Restrict-only: omitted/empty means every project in the department. */
  projectIds?: string[];
  /** Connectors this user may see and add (FAB-211 AC3). Restrict-only:
   *  omitted/empty inherits whatever their role/department/project allow. */
  connectorIds?: string[];
}

export interface Invite {
  id: string;
  name?: string;
  email: string;
  role: UserRole;
  departmentId?: string;
  status: string;
  inviteDate?: string;
  projectId?: string;
  /** dept_admin's saved project scope — empty means the whole department. */
  projectIds?: string[];
  /** Saved connector scope (FAB-211 AC3) — empty means no restriction. */
  connectorIds?: string[];
  /** The row is created even when the mail fails, so `false` means
   *  "recorded, not delivered" — fall back to `inviteUrl`. */
  emailSent?: boolean;
  emailError?: string;
  /** Signup link carried by the email — shown/copyable when delivery failed. */
  inviteUrl?: string;
}

/** Outcome of an invite delivery attempt (resend / org-key invite). */
export interface InviteDelivery {
  id?: string;
  status?: string;
  email?: string;
  invited?: boolean;
  emailSent?: boolean;
  emailError?: string;
  inviteUrl?: string;
}

/* ------------------------------------------------------------------ *\
   10.3 Departments
\* ------------------------------------------------------------------ */

/** Per-project grants. Each list is restrict-only: empty = no restriction (all
 *  allowed / inherit the parent); non-empty = only the listed items, and never
 *  beyond what the tier above (dept, then org) already allows. */
export interface ProjectGrants {
  /** Fabric modules (nav.* capability groups). */
  modules?: string[];
  connectors: string[];
  models: string[];
  entityTypes: string[];
  /** Governed connector-action ids/patterns (e.g. "stripe.refund", "slack.*"). */
  actions?: string[];
}

export interface DepartmentScopes {
  /** Fabric modules this department may use (the top grant tier). */
  modules?: string[];
  connectors: string[];
  entityTypes: string[];
  models: string[];
  /** Governed connector-action ids/patterns this department may run. */
  actions?: string[];
  /** Project access allow-list (project ObjectIds). Restrict-only: empty = all
   *  org projects accessible; non-empty = only these. Optional on write —
   *  omitting it leaves the stored allow-list untouched (the list drawer edits
   *  connectors/models/entity-types only); the read map always returns it. */
  projects?: string[];
  /** Per-project grants keyed by project ObjectId. The Department Scopes tab
   *  configures ONE project at a time (radio); its connectors/models/entity-
   *  types apply to that project only. Sent as the full map on write (read-
   *  modify-write); the server keeps `projects` in sync with these keys. */
  projectScopes?: Record<string, ProjectGrants>;
}

/** A custom RBAC role (org-defined). `capabilities` gate the UI (nav.* + …);
 *  `scopes` are the access scopes used for ingestion + retrieval of users on this
 *  role (bounded by their department/project). `detail` = L2 clearances (pii/financial). */
export interface CustomRole {
  id: string;
  orgId: string;
  name: string;
  key: string;
  description: string;
  capabilities: string[];
  scopes: {
    modules: string[];
    connectors: string[];
    models: string[];
    entityTypes: string[];
    actions: string[];
    detail: string[];
  };
  createdAt?: string;
  updatedAt?: string;
}

export interface RoleInput {
  name: string;
  description?: string;
  capabilities?: string[];
  scopes?: Partial<CustomRole['scopes']>;
}

/** A row in the Roles management page: a built-in role (with any per-org override
 *  applied) or a custom role, tagged with how many members are assigned. */
export interface RoleCatalogEntry {
  id: string | null;
  key: string;
  name: string;
  description: string;
  builtin: boolean;
  editable: boolean;
  deletable: boolean;
  overridden?: boolean;
  capabilities: string[];
  scopes: CustomRole['scopes'];
  assignedCount: number;
}

export interface SettingsDepartment {
  id: string;
  orgId: string;
  name: string;
  ownerId?: string;
  ownerName?: string;
  memberCount: number;
  agentCount: number;
  connectorCount: number;
  scopes: DepartmentScopes;
  createdAt?: string;
  updatedAt?: string;
}

/* ------------------------------------------------------------------ *\
   10.4 AI Policy
\* ------------------------------------------------------------------ */

/**
 * Governance switches that used to be deployment env vars. `null` means the
 * organization has not chosen, and the deployment default applies.
 */
export interface AIPolicyEnforcement {
  enforceActions: boolean | null;
  failClosed: boolean | null;
  contentSafetyCheck: boolean | null;
  singleAdminMode: boolean | null;
  requiredApprovals: number | null;
  /** Field-level data access. Absent on harness builds that predate it. */
  dataScope?: AIPolicyDataScope;
}

/** Field-level data access switches. `null` = follow the deployment default. */
export type DataScopeEnforce = 'off' | 'shadow' | 'on';
export type DataScopeHiddenNodes = 'drop' | 'null';

export interface AIPolicyDataScope {
  tag: boolean | null;
  enforce: DataScopeEnforce | null;
  hiddenNodes: DataScopeHiddenNodes | null;
  blockMaskedFilters: boolean | null;
}

/** The value in force for each data-scope switch when the organization has not set it. */
export interface AIPolicyDataScopeDefaults {
  tag: boolean;
  enforce: DataScopeEnforce;
  hiddenNodes: DataScopeHiddenNodes;
  blockMaskedFilters: boolean;
}

/** Unclassified-data default: "" = deployment default (Internal on SaaS, Restricted on-prem). */
export type RoutingClassification = '' | 'Public' | 'Internal' | 'Restricted';

/** Per-org rollout of the routing resolver: "" = the deployment's ROUTING_RESOLVER. */
export type ResolverMode = '' | 'off' | 'report' | 'enforce';

/** Org model-routing settings (routing v2, harness `settings.RoutingSettings`). */
export interface RoutingSettings {
  unclassifiedDefault: RoutingClassification;
  /** Data class (lower case) to Public | Internal | Restricted, merged over the built-in map. */
  classMapping: Record<string, 'Public' | 'Internal' | 'Restricted'> | null;
  classifierEnabled: boolean;
  classifierModelId: string;
  /** Prompt/response capture in telemetry; null = deployment default (off). */
  promptCapture: boolean | null;
  /** Read-only for org admins: only a platform caller or super_admin may set it. */
  resolverMode: ResolverMode;
  /**
   * What to do when a prompt contains sensitive content of a class. A missing
   * class means "allow". Detected content can only make routing stricter.
   */
  sensitiveContent?: Partial<Record<SensitiveClass, SensitiveAction>> | null;
  /** Company-sensitive terms (case-insensitive whole-word match), 2..100 chars each, at most 500. */
  sensitiveTerms?: string[] | null;
  /** Regexes (RE2 syntax that Python also compiles), at most 200 chars each, at most 100. */
  sensitivePatterns?: string[] | null;
}

/** Sensitive content classes the prompt scan detects. */
export type SensitiveClass = 'secret' | 'pii' | 'phi' | 'financial' | 'confidential';

/**
 * Per-class action: allow; private_only = only private (BYO, local or
 * sovereign) models; sovereign_only = only sovereign (or on-prem) models;
 * block = refuse the call.
 */
export type SensitiveAction = 'allow' | 'private_only' | 'sovereign_only' | 'block';

export interface AIPolicy {
  orgId: string;
  modelUse: { allowExternalGateway: boolean; pinRestrictedToLocal: boolean; allowFineTuning: boolean };
  dataHandling: { piiRedactionExternal: boolean; quarantineFailedValidations: boolean };
  autonomyLimits: { maxAutonomousSpend: number; maxChainedActions: number };
  approvalThresholds: { gateMoneyMovement: boolean; gateExternalCommitments: boolean; gateEveryExternalEmail: boolean };
  enforcement?: AIPolicyEnforcement;
  /** Harness's deployment values for the publication switches (read-only). */
  enforcementDefaults?: { singleAdminMode: boolean; requiredApprovals: number; dataScope?: AIPolicyDataScopeDefaults };
  /** Model routing (routing v2). Absent on harness builds that predate it. */
  routing?: RoutingSettings;
  inheritance: 'restrict_only';
  updatedAt?: string;
}

/** data-service's deployment values for the runtime switches (read-only). */
export interface RuntimeEnforcementDefaults {
  enforceActions: boolean;
  failClosed: boolean;
  contentSafetyCheck: boolean;
}

/* ------------------------------------------------------------------ *\
   10.5 AI Constitution
\* ------------------------------------------------------------------ */

export interface AIConstitutionPrinciple {
  n: number;
  title: string;
  description: string;
  enforcedBy: string[];
}

export interface ConstitutionSigner {
  userId: string;
  name: string;
  role: UserRole | string;
}

export interface AIConstitution {
  orgId: string;
  version: number;
  ratifiedAt: string;
  signedOffBy: ConstitutionSigner[];
  principles: AIConstitutionPrinciple[];
  draft?: { principles: AIConstitutionPrinciple[]; proposedBy?: string; status: 'pending_signoff' };
}

export interface ConstitutionVersion {
  version: number;
  ratifiedAt: string;
  signedOffBy: ConstitutionSigner[];
}

/* ------------------------------------------------------------------ *\
   10.6 Usage & Cost
\* ------------------------------------------------------------------ */

export interface UsageBreakdownRow {
  label: string;
  percent: number;
  /** Credits consumed. The honest number — read this, not `cost`. */
  credits: number;
  /** Legacy: the same credits rendered with a "$" in front, which is not a
   *  currency figure. Retained only for older consumers. */
  cost: string;
}

/** Real per-call token/cost totals from the litellm gateway (`llm_usage_log`),
 *  separate from the alpha-scaled `credits` ledger above. */
export interface LlmUsageTotals {
  /** In-window calls with no project recorded. A project filter excludes them,
   *  so the page reports the count rather than letting them vanish. */
  unattributed?: number;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  costUsd: number;
  calls: number;
}

export interface Usage {
  orgId: string;
  credits: { used: number; total: number; resetsAt: string };
  byModel: UsageBreakdownRow[];
  byDepartment: UsageBreakdownRow[];
  byAgent?: UsageBreakdownRow[];
  byUser?: UsageBreakdownRow[];
  budget: { limit: number; alertAt: number; hardBlockAt: number };
  projectedExhaustAt?: string | null;
  llmUsage: LlmUsageTotals;
}

export type UsageGroupBy = 'model' | 'department' | 'agent' | 'user';

export interface BudgetInput {
  limit: number;
  alertAt: number;
  hardBlockAt: number;
}

/** One identified, recorded LLM call — every gateway completion writes one of
 *  these (`GET /orgs/{id}/usage/llm-calls`). */
export interface LlmCallRecord {
  id: string;
  createdAt: string;
  model: string;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  costUsd: number | null;
  status: 'success' | 'error';
  userId?: string;
}

export interface LlmCallsResponse {
  total: number;
  data: LlmCallRecord[];
}

/** DB (graph) usage rolled up across every project in the org — nodes,
 *  relationships, and storage (`GET /orgs/{id}/usage/db-usage`). */
export interface DbUsage {
  orgId: string;
  projectCount: number;
  projectsReporting: number;
  nodes: number;
  relationships: number;
  graphStorageMb: number | null;
  filesStorageMb: number;
}

/* ------------------------------------------------------------------ *\
   10.7 Billing
\* ------------------------------------------------------------------ */

export interface Invoice {
  id: string;
  period: string;
  amount: string;
  status: string;
  pdfUrl?: string;
}

export interface PaymentMethod {
  brand: string;
  last4: string;
  expiry: string;
  billingEmail: string;
}

export interface Billing {
  orgId: string;
  plan: string;
  price: string;
  term?: string;
  renewsAt: string;
  features: string[];
  invoices: Invoice[];
  paymentMethod?: PaymentMethod;
  overage: { thisMonth: string; projectedExhaustAt?: string };
  isEntitlement?: boolean;
}

/* ================================================================== *\
   Service
\* ================================================================== */

/** Filter-bar selection sent to the Usage endpoints.
 *
 * Department is resolved to its projects by the caller: credit_history has no
 * department field, and data-service does not own departments — identity does.
 * Sending projects keeps the server filtering on fields it actually stores. */
export interface UsageFilterParams {
  projectIds?: string[];
  userIds?: string[];
  models?: string[];
  dateFrom?: string;
  dateTo?: string;
}

/** Drop empty selections so an "All" filter is simply absent from the query
 * string rather than sent as an empty value the server would have to special-case. */
function usageFilterParams(f: UsageFilterParams): Record<string, string> {
  const out: Record<string, string> = {};
  if (f.projectIds?.length) out.project_ids = f.projectIds.join(',');
  if (f.userIds?.length) out.user_ids = f.userIds.join(',');
  if (f.models?.length) out.models = f.models.join(',');
  if (f.dateFrom) out.date_from = f.dateFrom;
  if (f.dateTo) out.date_to = f.dateTo;
  return out;
}

export const settingsService = {
  // ---- Org (identity) ----
  async getOrg(orgId: string): Promise<Organization> {
    const res = await identityClient.get(`/settings/organizations/${orgId}`, idConfig());
    return idUnwrap<Organization>(res.data);
  },
  async updateOrg(orgId: string, input: UpdateOrgInput): Promise<Organization> {
    const res = await identityClient.put(`/settings/organizations/${orgId}`, input, idConfig());
    return idUnwrap<Organization>(res.data);
  },
  async getOrgHealth(orgId: string): Promise<OrgHealth> {
    const res = await identityClient.get(`/settings/organizations/${orgId}/health`, idConfig());
    return idUnwrap<OrgHealth>(res.data);
  },
  // Reusable, non-expiring signup key for this org (org admin/super-admin
  // only). The key string alone does not admit anyone — each signup email
  // must also be explicitly invited via inviteToOrgAccessKey below.
  async getOrgAccessKey(orgId: string): Promise<{ key: string; orgId: string; createdAt: string }> {
    const res = await identityClient.get(`/settings/organizations/${orgId}/access-key`, idConfig());
    return idUnwrap<{ key: string; orgId: string; createdAt: string }>(res.data);
  },
  // Invites one teammate by email — they get a signup link with the key
  // pre-filled and never need to see/handle the raw key themselves.
  async inviteToOrgAccessKey(orgId: string, email: string): Promise<InviteDelivery> {
    const res = await identityClient.post(`/settings/organizations/${orgId}/access-key/invite`, { email }, idConfig());
    return idUnwrap<InviteDelivery>(res.data);
  },

  // ---- Users (identity) ----
  async listUsers(params: { q?: string; departmentId?: string; limit?: number; offset?: number } = {}): Promise<{ users: SettingsUser[]; total: number }> {
    const res = await identityClient.get('/settings/users', idConfig({ params }));
    return idUnwrap<{ users: SettingsUser[]; total: number }>(res.data);
  },
  async inviteUser(input: InviteInput): Promise<Invite> {
    const res = await identityClient.post('/settings/users/invite', input, idConfig());
    return idUnwrap<Invite>(res.data);
  },
  async resendInvite(inviteId: string): Promise<InviteDelivery> {
    const res = await identityClient.post('/settings/invites/resend', { inviteId }, idConfig());
    return idUnwrap<InviteDelivery>(res.data);
  },
  /** Withdraw a pending invitation: deletes the invite AND the allow-list entry
   *  that is what actually lets the address sign up. `inviteId` is the row id an
   *  Invited row carries in the users list. */
  async revokeInvite(inviteId: string): Promise<{ id: string; email: string; status: string }> {
    const res = await identityClient.delete(`/settings/invites/${inviteId}`, idConfig());
    return idUnwrap(res.data);
  },
  async updateUserRole(userId: string, input: { role: UserRole | string; departmentId?: string }): Promise<{ id: string; role: string; departmentId?: string }> {
    const res = await identityClient.put(`/settings/users/${userId}/role`, input, idConfig());
    return idUnwrap(res.data);
  },
  /** dept_admin is multi-department-capable — replaces the user's FULL set of
   *  administered departments with exactly `departmentIds`. */
  async setDeptAdminDepartments(userId: string, departmentIds: string[]): Promise<{ id: string; departmentIds: string[] }> {
    const res = await identityClient.put(`/settings/users/${userId}/dept-admin`, { departmentIds }, idConfig());
    return idUnwrap(res.data);
  },
  /** project_admin is multi-project-capable — replaces the user's FULL set of
   *  administered projects with exactly `projectIds`. */
  async setProjectAdminProjects(userId: string, projectIds: string[]): Promise<{ id: string; projectIds: string[] }> {
    const res = await identityClient.put(`/settings/users/${userId}/project-admin`, { projectIds }, idConfig());
    return idUnwrap(res.data);
  },
  /** Plain project membership — every role can be a member of specific projects,
   *  independent of admin status. Always available/optional, any role. */
  async setUserProjectMemberships(userId: string, projectIds: string[]): Promise<{ id: string; projectIds: string[] }> {
    const res = await identityClient.put(`/settings/users/${userId}/project-memberships`, { projectIds }, idConfig());
    return idUnwrap(res.data);
  },
  async suspendUser(userId: string): Promise<{ id: string; status: UserStatus }> {
    const res = await identityClient.post(`/settings/users/${userId}/suspend`, {}, idConfig());
    return idUnwrap<{ id: string; status: UserStatus }>(res.data);
  },
  async unsuspendUser(userId: string): Promise<{ id: string; status: UserStatus }> {
    const res = await identityClient.post(`/settings/users/${userId}/unsuspend`, {}, idConfig());
    return idUnwrap<{ id: string; status: UserStatus }>(res.data);
  },

  // ---- Departments (identity) ----
  async listDepartments(): Promise<SettingsDepartment[]> {
    const res = await identityClient.get('/settings/departments', idConfig());
    return idUnwrap<{ departments: SettingsDepartment[] }>(res.data).departments;
  },
  async createDepartment(input: { name: string; ownerId?: string }): Promise<SettingsDepartment> {
    const res = await identityClient.post('/settings/departments', input, idConfig());
    return idUnwrap<SettingsDepartment>(res.data);
  },
  async updateDepartment(id: string, input: { name?: string; ownerId?: string }): Promise<SettingsDepartment> {
    const res = await identityClient.put(`/settings/departments/${id}`, input, idConfig());
    return idUnwrap<SettingsDepartment>(res.data);
  },
  async deleteDepartment(id: string): Promise<void> {
    await identityClient.delete(`/settings/departments/${id}`, idConfig());
  },
  async updateDepartmentScopes(id: string, scopes: Partial<DepartmentScopes>): Promise<SettingsDepartment> {
    const res = await identityClient.put(`/settings/departments/${id}/scopes`, scopes, idConfig());
    return idUnwrap<SettingsDepartment>(res.data);
  },

  // ---- Custom RBAC roles (super/org-admin) ----
  async listRoles(): Promise<CustomRole[]> {
    const res = await identityClient.get('/settings/roles', idConfig());
    return idUnwrap<{ roles: CustomRole[] }>(res.data).roles ?? [];
  },
  /** Built-in + custom roles with assignment counts — for the Roles page. */
  async getRoleCatalog(): Promise<RoleCatalogEntry[]> {
    const res = await identityClient.get('/settings/roles/catalog', idConfig());
    return idUnwrap<{ roles: RoleCatalogEntry[] }>(res.data).roles ?? [];
  },
  /** Edit a built-in role's caps/scopes (upserts the per-org override). */
  async updateBuiltinRole(
    key: string,
    input: { capabilities?: string[]; scopes?: Partial<CustomRole['scopes']> },
  ): Promise<RoleCatalogEntry> {
    const res = await identityClient.put(`/settings/roles/builtin/${key}`, input, idConfig());
    return idUnwrap<RoleCatalogEntry>(res.data);
  },
  async createRole(input: RoleInput): Promise<CustomRole> {
    const res = await identityClient.post('/settings/roles', input, idConfig());
    return idUnwrap<CustomRole>(res.data);
  },
  async updateRole(id: string, input: Partial<RoleInput>): Promise<CustomRole> {
    const res = await identityClient.put(`/settings/roles/${id}`, input, idConfig());
    return idUnwrap<CustomRole>(res.data);
  },
  async deleteRole(id: string): Promise<void> {
    await identityClient.delete(`/settings/roles/${id}`, idConfig());
  },

  // ---- AI Policy (harness) ----
  getAiPolicy(orgId: string): Promise<AIPolicy> {
    return apiClient.get<AIPolicy>(`/api/v1/orgs/${orgId}/ai-policy`);
  },
  updateAiPolicy(orgId: string, policy: AIPolicy): Promise<AIPolicy> {
    return apiClient.put<AIPolicy>(`/api/v1/orgs/${orgId}/ai-policy`, policy);
  },
  /** What an organization that has not chosen gets for the runtime switches. */
  getRuntimeEnforcementDefaults(): Promise<RuntimeEnforcementDefaults> {
    return dataServiceClient.get<RuntimeEnforcementDefaults>('/governance/enforcement-defaults', dataAuth);
  },

  // ---- AI Constitution (harness) ----
  getConstitution(orgId: string): Promise<AIConstitution> {
    return apiClient.get<AIConstitution>(`/api/v1/orgs/${orgId}/constitution`);
  },
  getConstitutionVersions(orgId: string): Promise<ConstitutionVersion[]> {
    return apiClient.get<ConstitutionVersion[]>(`/api/v1/orgs/${orgId}/constitution/versions`);
  },
  proposeAmendment(orgId: string, body: { principles: AIConstitutionPrinciple[]; rationale?: string; proposedBy?: string }): Promise<AIConstitution> {
    return apiClient.post<AIConstitution>(`/api/v1/orgs/${orgId}/constitution/amendments`, body);
  },
  publishConstitution(orgId: string, body: { signedOffBy: ConstitutionSigner[] }): Promise<AIConstitution> {
    return apiClient.post<AIConstitution>(`/api/v1/orgs/${orgId}/constitution/publish`, body);
  },

  // ---- Usage & Cost (data-service) ----
  getUsage(orgId: string, by: UsageGroupBy = 'model', filters: UsageFilterParams = {}): Promise<Usage> {
    return dataServiceClient.get<Usage>(`/orgs/${orgId}/usage`, {
      ...dataAuth,
      params: { by, ...usageFilterParams(filters) },
    });
  },
  getLlmCalls(
    orgId: string,
    params: { limit?: number; skip?: number; model?: string } = {},
    filters: UsageFilterParams = {},
  ): Promise<LlmCallsResponse> {
    return dataServiceClient.get<LlmCallsResponse>(`/orgs/${orgId}/usage/llm-calls`, {
      ...dataAuth,
      params: { ...params, ...usageFilterParams(filters) },
    });
  },
  getDbUsage(orgId: string): Promise<DbUsage> {
    return dataServiceClient.get<DbUsage>(`/orgs/${orgId}/usage/db-usage`, dataAuth);
  },
  setBudget(orgId: string, budget: BudgetInput): Promise<Usage['budget']> {
    return dataServiceClient.put<Usage['budget']>(`/orgs/${orgId}/usage/budget`, budget, dataAuth);
  },

  // ---- Billing (data-service) ----
  getBilling(orgId: string): Promise<Billing> {
    return dataServiceClient.get<Billing>(`/orgs/${orgId}/billing`, dataAuth);
  },
  invoicePdfUrl(orgId: string, invoiceId: string): string {
    return `/data-api/orgs/${orgId}/billing/invoices/${invoiceId}/pdf`;
  },
  topup(orgId: string, pack: string): Promise<{ ok: boolean; added: number; total: number }> {
    return dataServiceClient.post<{ ok: boolean; added: number; total: number }>(`/orgs/${orgId}/billing/topup`, { pack }, dataAuth);
  },
  updatePaymentMethod(orgId: string, pm: PaymentMethod): Promise<PaymentMethod> {
    return dataServiceClient.put<PaymentMethod>(`/orgs/${orgId}/billing/payment-method`, pm, dataAuth);
  },
};

/** Role → status-pill tone (10-settings.md §"Shared components", roleTone map). */
export const roleTone: Record<string, 'primary' | 'info' | 'accent' | 'warning' | 'neutral'> = {
  super_admin: 'primary',
  // org_admin is functionally identical to super_admin (2026-07-23: "complete
  // control over everything") — same tone so the two read as equivalent.
  org_admin: 'primary',
  power_user: 'info',
  dept_admin: 'accent',
  project_admin: 'accent',
  compliance_officer: 'warning',
};
